import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { Modal, Field, Empty, label } from '../ui.jsx';
import { useConfirm, useToast } from '../feedback.jsx';
import { t } from '../../i18n/index.jsx';
import { IconPlus } from '../icons.jsx';
import FileViewer from '../FileViewer.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
export const OFFER_STATUSES = ['draft', 'sent', 'negotiating', 'accepted', 'rejected', 'expired'];
export const blankOffer = {
  title: '', project_id: '', client_id: '', amount: '', currency: 'EUR',
  status: 'draft', valid_until: '', notes: '',
};

/**
 * Create/edit an offer, with versioned file upload (same pattern as
 * ContractForm). Accepting an offer spawns a linked contract draft — once
 * accepted, the offer becomes read-only here and the modal offers a link to
 * the new contract instead of editable fields.
 */
export default function OfferForm({ initial, projects = [], clients = [], isAdmin = false, onClose, onSaved, onAccepted }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [viewFile, setViewFile] = useState(null);
  const [contractTitle, setContractTitle] = useState(null);
  const fileInput = useRef(null);
  const isNew = !initial.id;
  const isAccepted = form.status === 'accepted';
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!isNew) {
      api.get(`/offers/${initial.id}`).then((r) => {
        setFiles(r.files || []);
        setContractTitle(r.data.contract_title || null);
      });
    }
  }, [initial.id, isNew]);

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/offers', form);
      else await api.put(`/offers/${initial.id}`, form);
      toast(isNew ? t('Offer created') : t('Offer saved'));
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete offer'), message: t('Delete this offer and its files?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/offers/${initial.id}`);
    toast(t('Offer deleted'));
    onSaved();
  }

  async function accept() {
    if (!(await confirm({
      title: t('Accept offer'),
      message: t('Accept this offer and create a linked contract draft from it?'),
      confirmLabel: t('Accept'),
    }))) return;
    try {
      const res = await api.post(`/offers/${initial.id}/accept`);
      toast(t('Offer accepted — contract draft created'));
      onAccepted?.(res.contract_id);
    } catch (e) { toast(e.message, { tone: 'error' }); }
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await api.upload(`/offers/${initial.id}/files`, fd);
    setFiles((await api.get(`/offers/${initial.id}`)).files || []);
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <Modal title={isNew ? t('New offer') : form.title} onClose={onClose}
      dirty={JSON.stringify(form) !== JSON.stringify(initial)}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          {!isNew && !isAccepted && <button className="btn primary ghost" onClick={accept}>{t('Accept → create contract')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      {isAccepted && contractTitle && (
        <div className="alert success">{t('Accepted — linked contract: {title}', { title: contractTitle })}</div>
      )}
      <Field label={t('Title')} error={errors.title}><input value={form.title} onChange={set('title')} /></Field>
      <div className="grid-2">
        <Field label={t('Client')} error={errors.client_id}>
          <select value={form.client_id || ''} onChange={set('client_id')}>
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label={t('Project (optional)')}>
          <select value={form.project_id || ''} onChange={set('project_id')}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid-3">
        <Field label={t('Amount')}><input type="number" step="0.01" value={form.amount || ''} onChange={set('amount')} /></Field>
        <Field label={t('Currency')}><input value={form.currency} onChange={set('currency')} /></Field>
        <Field label={t('Status')}><select value={form.status} onChange={set('status')} disabled={isAccepted}>{OFFER_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select></Field>
      </div>
      <Field label={t('Valid until')} error={errors.valid_until}><input type="date" value={form.valid_until || ''} onChange={set('valid_until')} /></Field>
      <Field label={t('Notes')}><textarea rows={2} value={form.notes || ''} onChange={set('notes')} /></Field>

      {!isNew && (
        <div className="files-block">
          <div className="panel-head">
            <h4>{t('Files (versioned)')}</h4>
            <input ref={fileInput} type="file" hidden onChange={uploadFile} />
            <button className="btn ghost sm" type="button" onClick={() => fileInput.current?.click()}>
              <IconPlus width={13} height={13} /> {t('Upload file')}
            </button>
          </div>
          {files.length === 0 ? <Empty>{t('No files uploaded.')}</Empty> : (
            <ul className="line-list">
              {files.map((f) => (
                <li key={f.id}>
                  <div className="ll-main"><span className="ll-title">v{f.version} · {f.original_name}</span></div>
                  <div className="ll-meta">
                    <button type="button" className="btn ghost sm" onClick={() => setViewFile({ path: `/offer-files/${f.id}/download`, name: f.original_name })}>{t('View')}</button>
                    <a className="btn ghost sm" href={`${API_BASE}/offer-files/${f.id}/download`}>{t('Download')}</a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {viewFile && <FileViewer path={viewFile.path} name={viewFile.name} onClose={() => setViewFile(null)} />}
    </Modal>
  );
}
